<template>
  <!--
    删除分类时的「时间块处置」选择框。

    背景：分类被时间块引用时，如果直接删掉分类，这些块会失去归属。用户要求：删除已被使用的分类时，
    必须先让用户二选一 —— ① 把这些时间块转移到另一个分类；② 连同时间块一起删除。
    处置范围是**全部时间块**（跨周），由后端按 tag_id 一次性处理，见 TagAppService.removeWithBlockStrategy。

    ⚠️ 必须 append-to-body：本组件渲染在 <div class="list-card"> 里，而 `.task-nav` 是
    position:relative + z-index:100，会把 el-dialog 的 wrapper 困在 .frame 的层叠上下文里，
    遮罩（挂 body、z-index 2000）就会盖住整个弹窗 → 一片黑、点不动。
    详见 CategoryLegend.vue 里同一处注释。
  -->
  <el-dialog
    title="删除分类"
    :visible.sync="innerVisible"
    width="440px"
    class="cat-delete-dialog"
    append-to-body
    :close-on-click-modal="false"
  >
    <div class="cd-body">
      <p class="cd-tip">
        分类「<strong class="cd-cat">{{ categoryText }}</strong>」下还有
        <strong class="cd-count">{{ blockCount }}</strong> 个时间块，请选择处理方式：
      </p>

      <div class="cd-options">
        <!-- 选项一（默认）：转移到其他分类 —— 非破坏性，避免误删 -->
        <div
          class="cd-option"
          :class="{ active: mode === 'convert', disabled: !hasTargets }"
          :title="hasTargets ? '' : '没有其他分类可供转移'"
          @click="selectConvert"
        >
          <span class="cd-icon convert"><FaIcon :icon="faRightLeft" :size="16" /></span>
          <div class="cd-option-main">
            <div class="cd-option-title">转移到其他分类</div>
            <div class="cd-option-desc">
              {{ hasTargets ? '保留这些时间块，改成下面选择的分类' : '当前没有其他分类可供转移' }}
            </div>
          </div>
          <span v-if="mode === 'convert'" class="cd-check"><FaIcon :icon="faCheck" :size="11" /></span>
        </div>

        <!-- 选项二：删除这些时间块 -->
        <div
          class="cd-option danger"
          :class="{ active: mode === 'delete' }"
          @click="mode = 'delete'"
        >
          <span class="cd-icon delete"><FaIcon :icon="faTrashCan" :size="16" /></span>
          <div class="cd-option-main">
            <div class="cd-option-title">删除这些时间块</div>
            <div class="cd-option-desc">连同分类一起删除，删除后不可恢复</div>
          </div>
          <span v-if="mode === 'delete'" class="cd-check"><FaIcon :icon="faCheck" :size="11" /></span>
        </div>
      </div>

      <!-- 目标分类：只在「转移」时出现，避免把下拉框塞进选项卡片里显得拥挤 -->
      <div v-if="mode === 'convert' && hasTargets" class="cd-target">
        <div class="cd-target-label">目标分类</div>
        <el-select
          v-model="targetKey"
          size="small"
          class="cd-select"
          placeholder="选择目标分类"
        >
          <el-option
            v-for="opt in targetOptionsWithText"
            :key="opt.key"
            :label="opt.text"
            :value="opt.key"
          >
            <span
              class="cd-dot"
              :style="{
                display: 'inline-block',
                width: '9px',
                height: '9px',
                marginRight: '6px',
                borderRadius: '50%',
                verticalAlign: 'middle',
                background: opt.color || '#a0aec0'
              }"
            ></span>
            <span>{{ opt.text }}</span>
          </el-option>
        </el-select>
      </div>
    </div>

    <span slot="footer" class="cd-footer">
      <el-button size="small" @click="innerVisible = false">取消</el-button>
      <el-button
        size="small"
        :type="mode === 'delete' ? 'danger' : 'primary'"
        :disabled="!canConfirm"
        @click="onConfirm"
      >确定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { parseTagName } from './constants'
import FaIcon from '@/components/common/FaIcon.vue'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan'
import { faRightLeft } from '@fortawesome/free-solid-svg-icons/faRightLeft'
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck'

export default {
  name: 'CategoryDeleteDialog',
  components: { FaIcon },
  props: {
    /** 显隐（.sync） */
    visible: {
      type: Boolean,
      default: false
    },
    /** 正在删除的分类 { key, label, color } */
    category: {
      type: Object,
      default: null
    },
    /** 该分类被多少个时间块使用（后端统计，跨周） */
    blockCount: {
      type: Number,
      default: 0
    },
    /** 可转移的目标分类 [{ key, label, color }]（已排除被删分类本身） */
    targetOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // 'convert' 转移到其他分类 / 'delete' 连同时间块一起删除
      mode: 'convert',
      targetKey: '',
      // 图标定义挂到实例上，供模板 :icon 使用
      faTrashCan,
      faRightLeft,
      faCheck
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(v) {
        this.$emit('update:visible', v)
      }
    },
    /** 被删分类的纯名称（不含 'fa:book' 图标前缀） */
    categoryText() {
      return parseTagName(this.category && this.category.label ? this.category.label : '').text || '该分类'
    },
    /** 目标分类补上纯名称，供下拉框显示 */
    targetOptionsWithText() {
      return (this.targetOptions || []).map(opt => ({
        ...opt,
        text: parseTagName(opt.label || '').text
      }))
    },
    hasTargets() {
      return this.targetOptionsWithText.length > 0
    },
    canConfirm() {
      if (this.mode === 'delete') return true
      return this.hasTargets && !!this.targetKey
    }
  },
  watch: {
    // 每次打开都复位：有可转移分类时默认选「转移到其他分类」（非破坏性，避免误删时间块）；
    // 没有任何其他分类时才只能选「删除时间块」。
    visible: {
      immediate: true,
      handler(v) {
        if (!v) return
        const first = this.targetOptionsWithText[0]
        this.targetKey = first ? String(first.key) : ''
        this.mode = first ? 'convert' : 'delete'
      }
    }
  },
  methods: {
    selectConvert() {
      if (!this.hasTargets) return
      this.mode = 'convert'
    },
    onConfirm() {
      if (!this.canConfirm) return
      this.$emit('confirm', {
        action: this.mode,
        targetKey: this.mode === 'convert' ? String(this.targetKey) : null
      })
      this.innerVisible = false
    }
  }
}
</script>

<style scoped>
.cd-body{
  padding:0 0.125rem;
}
.cd-tip{
  margin:0 0 1rem;
  font-size:0.875rem;
  line-height:1.7;
  color:var(--lp-text,#26332b);
}
.cd-cat{
  color:var(--lp-text,#26332b);
}
.cd-count{
  color:var(--lp-primary-dark,#2f7a4a);
  font-size:1rem;
  padding:0 0.15rem;
}

/* ===== 两个选项：图标 + 标题 + 说明 + 选中勾 ===== */
.cd-options{
  display:flex;
  flex-direction:column;
  gap:0.625rem;
}
.cd-option{
  display:flex;
  align-items:center;
  gap:0.75rem;
  padding:0.75rem 0.875rem;
  border:1.5px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
  background:var(--lp-surface,#fff);
  cursor:pointer;
  transition:border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.cd-option:hover{
  border-color:var(--lp-primary,#4aa46a);
}
.cd-option.active{
  border-color:var(--lp-primary,#4aa46a);
  background:var(--lp-primary-soft,#e9f5ec);
  box-shadow:0 0 0 3px rgba(74,164,106,0.10);
}
.cd-option.danger:hover{
  border-color:#d9534f;
}
.cd-option.danger.active{
  border-color:#d9534f;
  background:#fdf0ef;
  box-shadow:0 0 0 3px rgba(217,83,79,0.10);
}
.cd-option.disabled{
  cursor:not-allowed;
  opacity:0.55;
}
.cd-option.disabled:hover{
  border-color:var(--lp-border,#e3efe6);
}
.cd-icon{
  flex:0 0 auto;
  width:32px;
  height:32px;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:0.5rem;
}
.cd-icon.convert{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.cd-icon.delete{
  background:#fdecec;
  color:#d9534f;
}
.cd-option-main{
  flex:1 1 auto;
  min-width:0;
}
.cd-option-title{
  font-size:0.875rem;
  font-weight:600;
  line-height:1.3;
  color:var(--lp-text,#26332b);
}
.cd-option-desc{
  margin-top:0.1875rem;
  font-size:0.75rem;
  line-height:1.4;
  color:var(--lp-text-2,#5f7268);
}
.cd-check{
  flex:0 0 auto;
  width:18px;
  height:18px;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:50%;
  background:var(--lp-primary,#4aa46a);
  color:#fff;
}
.cd-option.danger.active .cd-check{
  background:#d9534f;
}

/* ===== 目标分类下拉：只在「转移」时出现 ===== */
.cd-target{
  margin-top:0.875rem;
  padding-top:0.875rem;
  border-top:1px dashed var(--lp-border,#e3efe6);
}
.cd-target-label{
  margin-bottom:0.375rem;
  font-size:0.75rem;
  font-weight:600;
  color:var(--lp-text-2,#5f7268);
}
.cd-select{
  width:100%;
}

.cd-footer{
  display:flex;
  justify-content:flex-end;
  gap:0.5rem;
}
</style>
